import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { type AppCollection, type AppMember, type AppRecord, loadAppList, loadAppMembers } from '@/lib/app-records';
import { type AppReview, loadAppReviews } from '@/lib/app-reviews';
import { type AppLabel, loadAppLabels } from '@/lib/app-collaboration';

export interface AppLabelsState {
  labels: AppLabel[];
  byRecord: Record<string, string[]>;
  policy: 'managers' | 'members';
}

/**
 * Everything the app's screens read (list, people, labels, reviews, teams),
 * kept live, and the way they write: a change returns the new record,
 * a refused change reloads and says why. Shared by the list and object pages.
 */
export const useAppData = (appId: string, onAvailability?: (available: boolean, title: string | null, hasReviewGrid: boolean) => void) => {
  const [list, setList] = useState<{ collection: AppCollection; records: AppRecord[] } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [members, setMembers] = useState<AppMember[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [labels, setLabels] = useState<AppLabelsState>({ labels: [], byRecord: {}, policy: 'managers' });
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [reviews, setReviews] = useState<AppReview[]>([]);
  const [historyKey, setHistoryKey] = useState(0);

  const reloadReviews = useCallback(() => { void loadAppReviews(appId).then(setReviews).catch(() => setError('Les revues n’ont pas pu être chargées.')); }, [appId]);
  const reloadLabels = useCallback(() => { void loadAppLabels(appId).then(setLabels).catch(() => setError('Les étiquettes n’ont pas pu être chargées.')); }, [appId]);
  const reload = useCallback(async () => {
    try {
      const next = await loadAppList(appId);
      setList(next);
      onAvailability?.(next !== null, next?.collection.title ?? null, Boolean(next?.collection.reviewGrid));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Liste indisponible.');
    } finally {
      setLoaded(true);
    }
  }, [appId, onAvailability]);

  useEffect(() => {
    void reload();
    void loadAppMembers().then(setMembers).catch(() => setError('Les membres de l’équipe n’ont pas pu être chargés.'));
    reloadLabels();
    reloadReviews();
    void supabase.from('teams').select('id, name').is('archived_at', null).order('name')
      .then(({ data, error: readError }) => {
        if (readError) setError('Les équipes n’ont pas pu être chargées.');
        else setTeams((data ?? []) as Array<{ id: string; name: string }>);
      });
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    // Changes by others (and by runs) appear live.
    const channel = supabase.channel(`app-records-${appId}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_records', filter: `tool_app_id=eq.${appId}` }, () => { void reload(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [appId, reload, reloadLabels, reloadReviews]);

  const run = useCallback(async (action: () => Promise<AppRecord>) => {
    setError(null);
    try {
      const record = await action();
      setList((current) => current && ({ ...current, records: current.records.map((item) => item.id === record.id ? record : item) }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La modification n’a pas pu être enregistrée.');
      await reload();
    }
  }, [reload]);

  return {
    list, loaded, members, userId, error, setError, labels, teams, reviews, historyKey,
    bumpHistory: () => setHistoryKey((key) => key + 1),
    reload, reloadLabels, reloadReviews, run,
  };
};

export type AppData = ReturnType<typeof useAppData>;
